// Who in an account may do what, mirrored from functions/lib/roles.js and
// pinned in parity.test.ts. The server decides; the dashboard uses this only
// to hide what a role cannot do, so nobody is offered a button that refuses.

export type Role = "owner" | "admin" | "member" | "reporting";
export type Action = "read" | "edit" | "configure" | "alerts" | "integrations" | "manageBots" | "manageTeam" | "billingRead" | "owner";

export const ROLES: Role[] = ["owner", "admin", "member", "reporting"];

export const ROLE_NAMES: Record<Role, string> = { owner: "Owner", admin: "Admin", member: "Member", reporting: "Reporting" };

// What each role is for, in the words the Team page uses.
export const ROLE_BLURBS: Record<Role, string> = {
  owner: "Everything, including billing, plans and the team.",
  admin: "Sets up the assistants and their integrations, and invites members and reporting users.",
  member: "Keeps the assistants' knowledge up to date and answers customers in live chat. Changes no settings.",
  reporting: "Sees the assistants, conversations, usage and invoices. Changes nothing."
};

export const ACTIONS: Record<Action, Role[]> = {
  read: ["owner", "admin", "member", "reporting"],
  edit: ["owner", "admin", "member"],
  configure: ["owner", "admin"],
  alerts: ["owner", "admin", "member"],
  integrations: ["owner", "admin"],
  manageBots: ["owner", "admin"],
  manageTeam: ["owner", "admin"],
  billingRead: ["owner", "reporting"],
  owner: ["owner"]
};

export function isRole(role: unknown): role is Role {
  return ROLES.includes(role as Role);
}

export function normalizeRole(role: unknown): Role {
  return isRole(role) ? role : "reporting";
}

export function can(role: unknown, action: Action): boolean {
  return ACTIONS[action].includes(normalizeRole(role));
}

export function canManageRole(actorRole: unknown, targetRole: Role): boolean {
  const actor = normalizeRole(actorRole);
  if (actor === "owner") return true;
  if (actor === "admin") return targetRole === "member" || targetRole === "reporting";
  return false;
}
